<template>
  <div id="profile-page">
    <Profile
      v-if="artistData"
      :profileData="artistData"
      :uid="uid"
      :loading="loading"
      :is-own-profile="isOwnProfile"
      class="c-max-w"
      @openUsersList="openUsersList"
    />
    <LabelProfile
      v-else-if="labelData"
      :uid="uid"
      :labelData="labelData"
      :loading="loading"
      :is-own-profile="isOwnProfile"
      class="c-max-w"
      @openUsersList="openUsersList"
    />

    <h2 v-else-if="!loading" class="p-a pos-center bc-red p">
      Page Not Found!
    </h2>

    <Footer v-show="!loading" />

    <div v-show="loading" class="loading-page p-f w-100 h-100 d-f ai-c">
      <marquee-text :repeat="6" :paused="false">
        <h1 class="ws-pre tt-up text-outline m-r">Loading</h1>
      </marquee-text>
    </div>

    <!-- Users list sidebar -->
    <UsersListSidebar
      :open="!!usersListOpenFor"
      :title="usersListOpenFor"
      :users-list="usersList"
      @close="usersListOpenFor = ''"
      @user-click="openProfile"
    />
  </div>
</template>

<script>
import { auth, rtdb } from "@/db.js";
import { sanitizeNameFrom } from "@/common";
import Profile from "../components/profile/Profile2.vue";
import MarqueeText from "vue-marquee-text-component";
import Footer from "@/components/navigation/Footer.vue";
import LabelProfile from "@/components/profile/LabelProfile2.vue";
import { mapGetters, mapState } from "vuex";
import UsersListSidebar from "@/components/profile/UsersListSidebar.vue";

export default {
  name: "ProfilePage",
  components: {
    Profile,
    LabelProfile,
    Footer,
    MarqueeText,
    UsersListSidebar,
  },
  data() {
    return {
      uid: "",
      artistData: null,
      labelData: null,
      loading: false,
      urlId: null,
      usersListOpenFor: "",
      usersList: [],
    };
  },
  metaInfo() {
    return {
      title:
        this.artistData?.username || this.labelData?.labelName || "Profile",
    };
  },
  created() {
    this.loadUserData(this.$route.params.id);
  },
  methods: {
    loadUserData(id) {
      this.artistData = null;
      this.labelData = null;
      this.urlId = id;
      this.getUser();
    },
    getUser() {
      const urlId = this.urlId;
      if (!urlId) {
        return;
      }
      const encodedUsername = encodeURIComponent(urlId).replace(/\./g, "%2E");
      const sanitizedUrlId = sanitizeNameFrom(urlId).toLowerCase();

      // If seeing own profile, use data from state for faster loading and live changes
      if (
        this.userType === "artist" &&
        this.userSettings &&
        (encodedUsername === this.userSettings.usernameUrl ||
          sanitizedUrlId === this.userSettings.usernameUrl)
      ) {
        this.artistData = this.userSettings;
        this.uid = auth.currentUser?.uid;
        return;
      } else if (
        this.userType === "label" &&
        this.labelSettings &&
        urlId === this.labelSettings.username
      ) {
        this.labelData = this.labelSettings;
        this.uid = auth.currentUser?.uid;
        return;
      }

      const ref = rtdb.ref();
      this.loading = true;

      const tryArtistLookup = (lookupValue) => {
        return new Promise((resolve) => {
          ref
            .child("userSettings")
            .orderByChild("usernameUrl")
            .equalTo(lookupValue)
            .once("value", (snap) => {
              if (snap.hasChildren()) {
                const [uid, userData] = Object.entries(snap.val())[0];
                resolve({ uid, userData });
              } else {
                resolve(null);
              }
            });
        });
      };

      const tryLabelLookup = () => {
        return new Promise((resolve) => {
          rtdb
            .ref("labelSettings")
            .orderByChild("username")
            .equalTo(urlId)
            .once("value", (snap) => {
              if (snap.hasChildren()) {
                const data = Object.values(snap.val())[0];
                resolve(data);
              } else {
                resolve(null);
              }
            });
        });
      };

      tryArtistLookup(encodedUsername)
        .then((artistResult) => {
          if (artistResult) {
            this.uid = artistResult.uid;
            this.artistData = artistResult.userData;
            this.loading = false;
            return "found";
          }
          if (encodedUsername !== sanitizedUrlId) {
            return tryArtistLookup(sanitizedUrlId);
          }
          return null;
        })
        .then((artistResult) => {
          if (artistResult === "found") return;
          if (artistResult) {
            this.uid = artistResult.uid;
            this.artistData = artistResult.userData;
            this.loading = false;
            return;
          }
          return tryLabelLookup();
        })
        .then((labelData) => {
          if (labelData) {
            this.$store.commit("SET_PROFILES_CACHE", {
              uid: labelData.uid,
              data: { ...labelData, type: "label" },
            });
            this.uid = labelData.uid;
            this.labelData = labelData;
          }
          this.loading = false;
        })
        .catch((err) => {
          console.error(err);
          this.loading = false;
        });
    },
    openUsersList(payload) {
      this.usersList = payload.followers || payload.followings;
      this.usersListOpenFor = Object.keys(payload)[0];

      (this.usersList ?? []).forEach((uid) => {
        this.$store.dispatch("cacheUserData", { uid });
      });
    },
    openProfile(uid) {
      this.usersListOpenFor = "";

      const profileUrlId = this.getProfileUrlIdByUid(uid);
      if (profileUrlId) {
        this.$router.push({
          name: "Profile",
          params: {
            id: decodeURI(profileUrlId), // browser will auto encode
          },
        });
      }
    },
  },
  computed: {
    ...mapState(["userSettings", "labelSettings", "userType"]),
    ...mapGetters([
      "getProfileImageByUid",
      "getProfileNameByUid",
      "getProfileUrlIdByUid",
    ]),

    isOwnProfile() {
      return this.uid && this.uid === auth.currentUser?.uid;
    },
  },
  watch: {
    userSettings() {},
    labelSettings() {},
    userType() {},

    $route(to) {
      if (to.params.id !== this.urlId) {
        this.loadUserData(to.params.id);
      }
    },
  },
};
</script>
